import React, { useState } from 'react';
import { AlertTriangle, Calendar, User, MapPin, Phone, Filter, RefreshCw, X, RotateCcw, UserPlus, CheckCircle, AlertCircle } from 'lucide-react';
import { adminDashboardService } from '../../../services/adminDashboardService';

const IncidentDeliveryTasksTable = ({ incidents, agents, onRefresh, loading }) => {
  const [selectedAgent, setSelectedAgent] = useState('ALL');
  const [dateFilter, setDateFilter] = useState('');
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [newAgentId, setNewAgentId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  // Filter incidents based on selected filters
  const filteredIncidents = incidents?.filter(incident => {
    const agentMatch = selectedAgent === 'ALL' || incident?.agent?.id === selectedAgent;
    const dateMatch = !dateFilter || new Date(incident?.createdAt)?.toISOString()?.split('T')?.[0] === dateFilter;
    return agentMatch && dateMatch;
  });

  const getIncidentTypeBadge = (type) => {
    const typeConfig = {
      'CLIENT_ABSENT': { color: 'bg-yellow-100 text-yellow-800', label: 'Client absent' },
      'REFUS_CLIENT': { color: 'bg-red-100 text-red-800', label: 'Refus client' },
      'ADRESSE_INCORRECTE': { color: 'bg-orange-100 text-orange-800', label: 'Adresse incorrecte' },
      'ACCES_IMPOSSIBLE': { color: 'bg-pink-100 text-pink-800', label: 'Accès impossible' },
      'COLIS_ENDOMMAGE': { color: 'bg-purple-100 text-purple-800', label: 'Colis endommagé' },
      'AUTRE': { color: 'bg-gray-100 text-gray-800', label: 'Autre' }
    };

    const config = typeConfig?.[type] || { color: 'bg-gray-100 text-gray-800', label: type };
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const handleResolveIncident = async (incident) => {
    if (!window.confirm(`Confirmer la résolution de l'incident pour ${incident?.recipientName} ?\n\nCeci remettra le statut à "À préparer" et effacera les détails de l'incident.`)) {
      return;
    }

    setActionLoading(true);
    setActionError('');
    setActionSuccess('');

    try {
      await adminDashboardService?.resolveIncident(incident?.id);
      setActionSuccess('Incident résolu avec succès');
      setTimeout(() => {
        setActionSuccess('');
        onRefresh?.();
      }, 2000);
    } catch (err) {
      console.error('Error resolving incident:', err);
      setActionError(err?.message || 'Erreur lors de la résolution');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenReassignModal = (incident) => {
    setSelectedIncident(incident);
    setNewAgentId('');
    setShowReassignModal(true);
    setActionError('');
  };

  const handleReassignSubmit = async () => {
    if (!newAgentId) {
      setActionError('Veuillez sélectionner un agent');
      return;
    }

    if (newAgentId === selectedIncident?.agent?.id) {
      setActionError('L\'agent sélectionné est déjà assigné');
      return;
    }

    setActionLoading(true);
    setActionError('');
    setActionSuccess('');

    try {
      await adminDashboardService?.reassignDeliveryTask(selectedIncident?.id, newAgentId);
      setActionSuccess('Tâche réassignée avec succès');
      setTimeout(() => {
        setActionSuccess('');
        setShowReassignModal(false);
        setSelectedIncident(null);
        setNewAgentId('');
        onRefresh?.();
      }, 2000);
    } catch (err) {
      console.error('Error reassigning task:', err);
      setActionError(err?.message || 'Erreur lors de la réassignation');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="bg-red-100 rounded-lg p-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Incidents de livraison</h2>
                <p className="text-sm text-gray-600">
                  {filteredIncidents?.length} incident(s) {selectedAgent !== 'ALL' || dateFilter ? 'filtré(s)' : 'total'}
                </p>
              </div>
            </div>
            <button
              onClick={onRefresh}
              disabled={loading}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Alert Messages */}
          {actionError && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{actionError}</p>
            </div>
          )}

          {actionSuccess && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-800">{actionSuccess}</p>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtres:</span>
            </div>
            
            {/* Agent Filter */}
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e?.target?.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="ALL">Tous les agents</option>
              {agents?.map(agent => (
                <option key={agent?.id} value={agent?.id}>
                  {agent?.name}
                </option>
              ))}
            </select>

            {/* Date Filter */}
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e?.target?.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />

            {/* Reset Filters */}
            {(selectedAgent !== 'ALL' || dateFilter) && (
              <button
                onClick={() => {
                  setSelectedAgent('ALL');
                  setDateFilter('');
                }}
                className="flex items-center space-x-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-700 transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Réinitialiser</span>
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          {filteredIncidents?.length === 0 ? (
            <div className="p-12 text-center">
              <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">Aucun incident de livraison</p>
              <p className="text-sm text-gray-500 mt-1">
                {selectedAgent !== 'ALL' || dateFilter ? 'Essayez de modifier les filtres' : 'Les incidents apparaîtront ici'}
              </p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date / Agent
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Destinataire
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type d'incident
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commentaire
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Livraison prévue
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions Admin
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredIncidents?.map((incident) => (
                  <tr key={incident?.id} className="hover:bg-gray-50 transition-colors">
                    {/* Date & Agent */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-2">
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">
                            {new Date(incident?.createdAt)?.toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-700">{incident?.agent?.name}</span>
                        </div>
                      </div>
                    </td>

                    {/* Recipient Info */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium text-gray-900">{incident?.recipientName}</p>
                        <div className="flex items-center space-x-1">
                          <Phone className="w-3 h-3 text-gray-400" />
                          <p className="text-xs text-gray-600">{incident?.recipientPhone}</p>
                        </div>
                        <div className="flex items-start space-x-1 mt-1">
                          <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-gray-600 line-clamp-2">{incident?.deliveryAddress}</p>
                        </div>
                      </div>
                    </td>

                    {/* Incident Type */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getIncidentTypeBadge(incident?.incidentType)}
                    </td>

                    {/* Comment */}
                    <td className="px-6 py-4">
                      <div className="max-w-xs">
                        <p className="text-sm text-gray-900 line-clamp-3">{incident?.incidentComment}</p>
                      </div>
                    </td>

                    {/* Planned Delivery */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm text-gray-900">
                          {new Date(incident?.plannedDate)?.toLocaleDateString('fr-FR')}
                        </p>
                        <p className="text-xs text-gray-600">{incident?.plannedTimeWindow}</p>
                      </div>
                    </td>

                    {/* Admin Actions */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-2">
                        <button
                          onClick={() => handleResolveIncident(incident)}
                          disabled={actionLoading}
                          className="flex items-center justify-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                        >
                          <RotateCcw className="w-4 h-4" />
                          <span>Résoudre</span>
                        </button>
                        <button
                          onClick={() => handleOpenReassignModal(incident)}
                          disabled={actionLoading}
                          className="flex items-center justify-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-sm"
                        >
                          <UserPlus className="w-4 h-4" />
                          <span>Réassigner</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Reassign Modal */}
      {showReassignModal && selectedIncident && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="bg-blue-100 rounded-lg p-2">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Réassigner la livraison</h3>
              </div>
              <button
                onClick={() => setShowReassignModal(false)}
                disabled={actionLoading}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Current Assignment Info */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-600 mb-2">Tâche actuelle:</p>
                <p className="text-sm font-medium text-gray-900">{selectedIncident?.recipientName}</p>
                <p className="text-xs text-gray-600 mt-1">Agent actuel: {selectedIncident?.agent?.name}</p>
              </div>

              {/* New Agent Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nouvel agent <span className="text-red-600">*</span>
                </label>
                <select
                  value={newAgentId}
                  onChange={(e) => setNewAgentId(e?.target?.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Sélectionner un agent</option>
                  {agents?.filter(a => a?.id !== selectedIncident?.agent?.id)?.map(agent => (
                    <option key={agent?.id} value={agent?.id}>
                      {agent?.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Warning Message */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Attention:</strong> La réassignation de cette tâche notifiera le nouvel agent.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowReassignModal(false)}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  Annuler
                </button>
                <button
                  onClick={handleReassignSubmit}
                  disabled={actionLoading || !newAgentId}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {actionLoading ? 'Réassignation...' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default IncidentDeliveryTasksTable;